// src/pages/services/PanchakarmaTherapy.jsx
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import leaf from '../../assets/leaf1.png';
import heroImg from '../../assets/A-image6.png';
import doctorImg from '../../assets/doctors4.png';
import Navbar from '../../common/Navbar';
import Footer from '../../common/Footer';

const PanchakarmaTherapy = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white text-sm md:text-base leading-relaxed">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-screen flex flex-col items-center justify-center overflow-hidden bg-gradient-to-b from-[#E8E0D5] to-[#F8F3EA] px-4 sm:px-6">
        <div className="absolute inset-0 bg-black/40 z-10"></div>
        <img 
          src={heroImg} 
          alt="Panchakarma Therapy" 
          className="absolute inset-0 w-full h-full object-cover"
        />
        
        <div className="relative z-20 text-center max-w-4xl sm:max-w-5xl mx-auto flex flex-col items-center px-4 sm:px-6">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-playfair font-bold text-white mb-4 sm:mb-6 drop-shadow-2xl leading-tight">
            Panchakarma Therapy
          </h1>
          <p className="text-lg md:text-2xl lg:text-3xl text-white/95 font-light leading-relaxed text-center">
            The ultimate Ayurvedic detoxification and rejuvenation program — cleanse deeply, renew completely.
          </p>
        </div>

        {/* Floating leaves */}
        <img src={leaf} className="absolute top-20 left-10 w-28 sm:w-32 opacity-30 animate-float" />
        <img src={leaf} className="absolute bottom-32 right-16 w-36 sm:w-40 opacity-25 animate-float" style={{ animationDelay: '2s' }} />
        <img src={leaf} className="absolute top-1/2 -left-16 sm:-left-20 w-40 sm:w-48 opacity-20 animate-float" style={{ animationDelay: '4s' }} />
      </section>

      {/* Benefits Section */}
      <section className="py-24 px-4 sm:px-6 bg-[#F8F3EA]">
        <div className="max-w-6xl mx-auto flex flex-col items-center">
          <h2 className="text-4xl md:text-5xl font-playfair text-center text-[#0B2A2C] mb-16">
            Transformative Benefits
          </h2>
          <div className="grid md:grid-cols-3 gap-10">
            {[
              { icon: "", title: "Deep Detoxification", desc: "Removes Ama (toxins) from every cell" },
              { icon: "", title: "Dosha Balance", desc: "Restores equilibrium to Vata, Pitta & Kapha" },
              { icon: "", title: "Rejuvenation", desc: "Renews tissues, boosts immunity & vitality" },
              { icon: "", title: "Mental Clarity", desc: "Calms mind, reduces stress & anxiety" },
              { icon: "", title: "Disease Prevention", desc: "Strengthens body’s natural healing power" },
              { icon: "", title: "Youthful Glow", desc: "Improves skin, hair & overall radiance" }
            ].map((b, i) => (
              <div 
                key={i} 
                className="flex-1 min-w-[220px] max-w-sm bg-white rounded-3xl p-6 sm:p-10 shadow-xl hover:shadow-2xl transition-all hover:-translate-y-2 sm:hover:-translate-y-4 border border-[#E4D7C2]/50 flex flex-col items-center text-center"
              >
                <div className="text-6xl mb-6">{b.icon}</div>
                <h3 className="text-2xl font-bold text-[#0B2A2C] mb-4">{b.title}</h3>
                <p className="text-gray-700 text-base leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The 5 Stages of Panchakarma */}
      <section className="py-24 px-4 sm:px-6 bg-[#E4D7C2]">
        <div className="max-w-5xl mx-auto flex flex-col gap-8 sm:gap-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-playfair text-center text-[#0B2A2C] mb-8 sm:mb-20">
            The Five Sacred Actions
          </h2>
          <div className="flex flex-col gap-8">
            {[
              { stage: "Purvakarma", desc: "Preparation with oleation & fomentation" },
              { stage: "Vamana", desc: "Therapeutic vomiting to clear Kapha" },
              { stage: "Virechana", desc: "Purgation to eliminate excess Pitta" },
              { stage: "Basti", desc: "Medicated enema — king of all treatments" },
              { stage: "Nasya & Raktamokshana", desc: "Nasal therapy & blood cleansing" }
            ].map((item, i) => (
              <div key={i} className="flex flex-col sm:flex-row items-start sm:items-center gap-6 sm:gap-10">
                <div className="w-16 h-16 sm:w-24 sm:h-24 bg-[#0B2A2C] text-white rounded-full flex items-center justify-center text-xl sm:text-3xl font-bold shadow-md sm:shadow-2xl flex-shrink-0 mx-auto sm:mx-0">
                  {i + 1}
                </div>
                <div className="flex-col text-center mx-auto flex-1 bg-white items-center rounded-3xl p-8 sm:p-10 shadow-xl border border-[#E4D7C2] text-center sm:text-left">
                  <h4 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#0B2A2C] mb-2 sm:mb-3">
                    {item.stage}
                  </h4>
                  <p className="text-sm sm:text-base md:text-base text-gray-700 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Doctor Quote */}
      <section className="py-24 px-4 sm:px-6 bg-gradient-to-r from-[#0B2A2C] to-[#1A4D4F] text-white text-center flex flex-col items-center">
        <img 
          src={doctorImg} 
          alt="Dr. V. K. Subrahmanyeswara Rao" 
          className="w-32 h-32 rounded-full mx-auto mb-8 sm:mb-12 border-4 sm:border-8 border-amber-300/30 shadow-2xl"
        />
        <blockquote className="text-2xl md:text-4xl font-joan italic max-w-4xl mx-auto leading-relaxed">
          “Panchakarma is not just a treatment — it is the complete reset your body and soul have been waiting for.”
        </blockquote>
        <p className="text-sm sm:text-base md:text-lg opacity-90">
          — Dr. V. K. Subrahmanyeswara Rao, MD (Ayurveda) Gold Medalist
        </p>
      </section>

      {/* Final CTA */}
<section className="py-12 sm:py-28 px-4 sm:px-6 text-center flex flex-col items-center gap-5 sm:gap-8 bg-[#F8F3EA]">
  <h2 className="text-3xl text-center sm:text-4xl md:text-5xl font-playfair text-[#0B2A2C] mb-4 sm:mb-8 leading-snug sm:leading-tight">
    Begin Your Deep Cleanse Today
  </h2>

  <p className="text-sm sm:text-base md:text-lg text-gray-700 max-w-md sm:max-w-2xl text-center leading-relaxed sm:leading-loose">
    Experience the most authentic Panchakarma under expert guidance
  </p>

  <button 
    onClick={() => navigate("/BookNow")}
    className="bg-[#0B2A2C] text-white px-10 sm:px-16 py-4 sm:py-6 rounded-full text-lg sm:text-xl md:text-2xl font-medium hover:bg-[#1A4D4F] transition-all hover:shadow-2xl hover:scale-105"
  >
    Book Panchakarma Program
  </button>
</section>



      <Footer />
    </div>
  );
};

export default PanchakarmaTherapy;
